import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { authApiCall } from '../../../services/http';
import { endpoints } from '../../../config';
import { productLabel } from '../../../helpers/products';

// Opción de producto en los Autocomplete: displayName arriba, slug en gris pequeño debajo.
const renderProductOption = (props, option) => (
  <Box component="li" {...props} key={option.slug}>
    <Box>
      <Typography variant="body2">{productLabel(option)}</Typography>
      <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontSize: 11 }}>
        {option.slug}
      </Typography>
      {/* Nota interna del catálogo (Product.comments): solo llega con token de ADMIN. */}
      {option.comments && (
        <Typography
          variant="caption"
          display="block"
          sx={{
            color: 'text.disabled',
            fontSize: 11,
            fontStyle: 'italic',
            maxWidth: 320,
            whiteSpace: 'normal',
          }}
        >
          {option.comments}
        </Typography>
      )}
    </Box>
  </Box>
);

EditSeatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  seat: PropTypes.object,
  plans: PropTypes.array,
  addons: PropTypes.array,
  productsLoading: PropTypes.bool,
  apiProps: PropTypes.object.isRequired,
  onSaved: PropTypes.func,
};

// Edita el plan/add-ons de un seat existente (admin global): PUT /seats/:id.
// No cambia el usuario asignado (para eso: unassign + assign).
export default function EditSeatDialog({
  open,
  onClose,
  seat,
  plans = [],
  addons = [],
  productsLoading,
  apiProps,
  onSaved,
}) {
  const [plan, setPlan] = useState(null);
  const [seatAddons, setSeatAddons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !seat) return;
    setError(null);
    setSaving(false);
    setPlan(plans.find((p) => p.slug === seat.planSlug) || null);
    const current = seat.addonSlugs || [];
    setSeatAddons(addons.filter((a) => current.includes(a.slug)));
  }, [open, seat, plans, addons]);

  if (!seat) return null;

  const save = async () => {
    if (!plan) {
      setError('Selecciona un plan.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // PUT /seats/:id — cambia planSlug y/o addonSlugs. Re-valida catálogo/compatibilidad.
      const res = await authApiCall({
        method: 'PUT',
        endpoint: endpoints.seats,
        path: `/${seat.id}`,
        data: { planSlug: plan.slug, addonSlugs: seatAddons.map((a) => a.slug) },
        ...apiProps,
      });
      if (onSaved) onSaved(res?.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Error al actualizar el seat');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Editar plan / add-ons del seat</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Seat {String(seat.id).slice(0, 8)} · solo cambia el plan y los add-ons (no reasigna el usuario).
          </Typography>
          <Autocomplete
            fullWidth
            options={plans}
            loading={productsLoading}
            value={plan}
            onChange={(e, v) => setPlan(v)}
            getOptionLabel={productLabel}
            renderOption={renderProductOption}
            isOptionEqualToValue={(o, v) => o.slug === v.slug}
            size="small"
            renderInput={(params) => <TextField {...params} label="Plan" placeholder="Selecciona plan" />}
          />
          <Autocomplete
            multiple
            options={addons}
            loading={productsLoading}
            value={seatAddons}
            onChange={(e, v) => setSeatAddons(v)}
            getOptionLabel={productLabel}
            renderOption={renderProductOption}
            isOptionEqualToValue={(o, v) => o.slug === v.slug}
            size="small"
            renderTags={(value, getTagProps) =>
              value.map((a, index) => (
                <Chip size="small" label={productLabel(a)} {...getTagProps({ index })} key={a.slug} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Add-ons" placeholder="Añade add-ons" />}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <LoadingButton variant="contained" onClick={save} loading={saving}>
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
